import React, { useState, useEffect } from 'react';
import { FaTimes } from 'react-icons/fa';
import { useDashboard } from '../../context/DashboardContext';
import { useChat } from '../../context/ChatContext';
import api from '../../api';
import { formatTimeAgo } from '../../utils/dateUtils';
import { getEcho } from '../../echo';
import { useI18n } from '../../i18n';
import { resolveNotificationNav } from '../../constants/notificationNavMap';
import './NotificationSidebar.css';

const NotificationAvatar = ({ avatarUrl, name }) => {
    if (avatarUrl) {
        return <img src={avatarUrl} alt={name || ''} className="notification-avatar" />;
    }
    const initial = name ? name.charAt(0) : '?';
    return <div className="notification-avatar-initials">{initial}</div>;
};

const NotificationSidebar = () => {
    const { t } = useI18n();
    const { isNotificationsOpen, setIsNotificationsOpen, setActiveView, setDeepLink } = useDashboard();
    const { setActiveConversationId } = useChat();
    const [notifications, setNotifications] = useState([]);

    const fetchNotifications = async () => {
        try {
            const response = await api.get('/notifications?limit=20');
            if (response.status) {
                setNotifications(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching notifications:', error);
        }
    };

    useEffect(() => {
        if (isNotificationsOpen) {
            fetchNotifications();
        }

        const userStr = localStorage.getItem('user');
        if (!userStr) return;

        const user = JSON.parse(userStr);
        const userId = user.id;

        if (userId && isNotificationsOpen) {
            const echo = getEcho();

            if (!echo) {
                console.error('Echo not initialized in sidebar - user may not be authenticated');
                return;
            }

            echo.private(`App.Models.User.${userId}`)
                .notification((notification) => {
                    setNotifications(prev => [{
                        id: notification.id,
                        data: {
                            link: notification.link,
                            act: notification.act,
                            msg: notification.msg,
                            actor_name: notification.actor_name,
                            actor_avatar: notification.actor_avatar,
                            target_id: notification.target_id,
                            target_type: notification.target_type,
                            target_meta: notification.target_meta,
                        },
                        created_at: notification.created_at,
                        read_at: notification.read_at,
                    }, ...prev.slice(0, 19)]);
                })
                .error((error) => {
                    console.error('Failed to subscribe to notification channel in sidebar:', error);
                });
        }

        return () => {
            if (userId) {
                const echo = getEcho();
                if (echo) {
                    echo.leave(`App.Models.User.${userId}`);
                }
            }
        };
    }, [isNotificationsOpen]);

    const handleNotificationClick = async (notif) => {
        if (!notif.read_at) {
            try {
                await api.post(`/notifications/${notif.id}/read`);
                setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, read_at: new Date().toISOString() } : n));
            } catch (error) {
                console.error('Error marking notification as read:', error);
            }
        }

        const nav = resolveNotificationNav(notif);

        if (nav?.view) {
            setActiveView(nav.view);
            if (nav.deepLink?.target_type === 'conversation') {
                setActiveConversationId(nav.deepLink.target_id);
            } else if (nav.deepLink) {
                setDeepLink(nav.deepLink);
            }
            setIsNotificationsOpen(false);
            return;
        }

        // Legacy fallback: link field (e.g. 'talk:42')
        if (notif.data?.link) {
            const [module, id] = notif.data.link.split(':');
            if (module === 'talk') {
                setActiveView('talk');
                if (id) setActiveConversationId(parseInt(id, 10));
                setIsNotificationsOpen(false);
            }
        }
    };

    if (!isNotificationsOpen) return null;

    return (
        <div className="notification-sidebar-overlay" onClick={() => setIsNotificationsOpen(false)}>
            <div className="notification-sidebar" onClick={(e) => e.stopPropagation()}>
                <div className="sidebar-header">
                    <h2>{t('navigation.notifications')}</h2>
                    <button className="close-sidebar-btn" onClick={() => setIsNotificationsOpen(false)}>
                        <FaTimes />
                    </button>
                </div>

                <div className="sidebar-content">
                    <div className="sidebar-notification-list">
                        {notifications.length > 0 ? (
                            notifications.map((notification) => (
                                <div
                                    key={notification.id}
                                    className={`sidebar-notification-item ${!notification.read_at ? 'unread' : ''}`}
                                    onClick={() => handleNotificationClick(notification)}
                                >
                                    <div className="notification-avatar-wrapper">
                                        <NotificationAvatar
                                            avatarUrl={notification.data.actor_avatar}
                                            name={notification.data.actor_name || notification.data.act}
                                        />
                                        <span className="status-dot"></span>
                                    </div>
                                    <div className="notification-content">
                                        <p className="notification-text">
                                            <strong>{notification.data.actor_name || notification.data.act}</strong>
                                        </p>
                                        <p className="notification-time">{notification.data.msg}</p>
                                        <div className="notification-project">
                                            {formatTimeAgo(notification.created_at)}
                                        </div>
                                    </div>
                                    {!notification.read_at && <div className="unread-indicator"></div>}
                                </div>
                            ))
                        ) : (
                            <div className="empty-notifications">
                                {t('common.notifications.none')}
                            </div>
                        )}
                    </div>
                </div>

            </div>
        </div>
    );
};

export default NotificationSidebar;
